import { isTuningIdentity } from './identity';
import type { TuningIdentity } from './identity';
import { hasOnlyTuningKeys, isTuningEnum, isTuningInteger, isTuningNumber, isTuningRecord, isTuningString, tuningJsonBytes } from './validation';

export const TUNING_TELEMETRY_MAX_BYTES = 4_096;
export const TUNING_CONTROL_MAX_BYTES = 16_384;
export const TUNING_ENDPOINT_BYTES_PER_SECOND = 8_192;
export const TUNING_TELEMETRY_BUFFER_MAX_BYTES = 65_536;
export const TUNING_CONTROL_QUEUE_MAX = 32;
export type TuningMetric = {
  value: number | null; unit: 'ms' | 'fps' | 'bps' | 'bytes' | 'count' | 'percent';
  availability: 'measured' | 'unsupported' | 'missing' | 'stale' | 'not-sampled';
  source: string; sampleCount: number; semantics: 'interval-delta' | 'cumulative' | 'gauge' | 'distribution';
};
export type TuningHistogram = {
  schema: 'ms-v1'; unit: 'ms'; source: string; availability: TuningMetric['availability'];
  monotonicStartMs: number; monotonicEndMs: number;
  buckets: readonly (readonly [index: number, count: number])[]; sampleCount: number; maxMs: number | null;
};
export type TuningTelemetryWindow = {
  version: 1; target: TuningIdentity; endpoint: 'host' | 'guest'; sequence: number;
  monotonicStartMs: number; monotonicEndMs: number; wallAnchorMs: number;
  clock: { state: 'calibrated' | 'uncalibrated' | 'stale'; offsetMs?: number; uncertaintyMs?: number; ageMs?: number };
  pathEpoch: number; captureGeneration?: number; sourceId?: string; endpointConnectionEpoch: number;
  metrics: Readonly<Record<string, TuningMetric>>; distributions: Readonly<Record<string, TuningHistogram>>;
  context?: TuningTelemetryContext;
  telemetryDropped: number; sampleSkipped: number; visibility: string;
};
export type TuningTelemetryContext = {
  codec?: string; codecProfile?: string; encoderImplementation?: string; decoderImplementation?: string;
  route?: 'direct' | 'relay' | 'unknown'; recoveryState?: string; recoveryReason?: string;
  policyVersion?: string; buildId?: string; runtimeId?: string; configurationHash?: string;
};
const availabilities = ['measured', 'unsupported', 'missing', 'stale', 'not-sampled'];
export const TUNING_HISTOGRAM_EDGES_MS: readonly number[] = Object.freeze([
  ...Array.from({ length: 257 }, (_, i) => i),
  ...Array.from({ length: 96 }, (_, i) => 264 + i * 8),
  ...Array.from({ length: 112 }, (_, i) => 1_088 + i * 64),
  Infinity,
]);
export const TUNING_HISTOGRAM_OVERFLOW_INDEX = TUNING_HISTOGRAM_EDGES_MS.length - 2;

/** Constant-time lookup into left-closed/right-open ms-v1 bins. */
export function tuningHistogramBucketIndex(milliseconds: number): number {
  if (!isTuningNumber(milliseconds)) throw new RangeError('Histogram observation must be finite and nonnegative');
  if (milliseconds < 256) return Math.floor(milliseconds);
  if (milliseconds < 1_024) return 256 + Math.floor((milliseconds - 256) / 8);
  if (milliseconds < 8_192) return 352 + Math.floor((milliseconds - 1_024) / 64);
  return TUNING_HISTOGRAM_OVERFLOW_INDEX;
}

export function isTuningMetric(value: unknown): value is TuningMetric {
  if (!isTuningRecord(value) || !hasOnlyTuningKeys(value,
    ['value', 'unit', 'availability', 'source', 'sampleCount', 'semantics'])) return false;
  return isTuningEnum(value['unit'], ['ms', 'fps', 'bps', 'bytes', 'count', 'percent']) &&
    isTuningEnum(value['availability'], availabilities) && isTuningString(value['source'], 256) &&
    isTuningInteger(value['sampleCount']) && isTuningEnum(value['semantics'], ['interval-delta', 'cumulative', 'gauge', 'distribution']) &&
    (value['availability'] === 'measured' ? typeof value['value'] === 'number' && Number.isFinite(value['value']) : value['value'] === null);
}

export function isTuningHistogram(value: unknown): value is TuningHistogram {
  if (!isTuningRecord(value) || !hasOnlyTuningKeys(value,
    ['schema', 'unit', 'source', 'availability', 'monotonicStartMs', 'monotonicEndMs', 'buckets', 'sampleCount', 'maxMs'])) return false;
  if (value['schema'] !== 'ms-v1' || value['unit'] !== 'ms' || !isTuningString(value['source'], 256) ||
    !isTuningEnum(value['availability'], availabilities) || !isTuningNumber(value['monotonicStartMs']) ||
    !isTuningNumber(value['monotonicEndMs'], value['monotonicStartMs']) || !isTuningInteger(value['sampleCount']) ||
    !Array.isArray(value['buckets']) || value['buckets'].length > TUNING_HISTOGRAM_OVERFLOW_INDEX + 1) return false;
  let count = 0; let lastIndex = -1;
  for (const bucket of value['buckets']) {
    if (!Array.isArray(bucket) || bucket.length !== 2 || !isTuningInteger(bucket[0], 0, TUNING_HISTOGRAM_OVERFLOW_INDEX) ||
      bucket[0] <= lastIndex || !isTuningInteger(bucket[1], 1)) return false;
    lastIndex = bucket[0]; count += bucket[1];
  }
  if (!Number.isSafeInteger(count) || count !== value['sampleCount']) return false;
  if (value['sampleCount'] === 0) return value['maxMs'] === null;
  return value['availability'] === 'measured' && isTuningNumber(value['maxMs']) &&
    tuningHistogramBucketIndex(value['maxMs']) === lastIndex;
}

function isClock(value: unknown): boolean {
  if (!isTuningRecord(value) || !hasOnlyTuningKeys(value, ['state', 'offsetMs', 'uncertaintyMs', 'ageMs'])) return false;
  const offset = value['offsetMs'] === undefined || (typeof value['offsetMs'] === 'number' && Number.isFinite(value['offsetMs']));
  const uncertainty = value['uncertaintyMs'] === undefined || isTuningNumber(value['uncertaintyMs']);
  const age = value['ageMs'] === undefined || isTuningNumber(value['ageMs']);
  return isTuningEnum(value['state'], ['calibrated', 'uncalibrated', 'stale']) && offset && uncertainty && age &&
    (value['state'] !== 'calibrated' || (value['offsetMs'] !== undefined && value['uncertaintyMs'] !== undefined && value['ageMs'] !== undefined));
}

function isMetricMap(value: unknown, guard: (item: unknown) => boolean): boolean {
  return isTuningRecord(value) && Object.keys(value).length <= 64 &&
    Object.entries(value).every(([key, item]) => isTuningString(key) && guard(item));
}

export function isTuningTelemetry(value: unknown): value is TuningTelemetryWindow {
  if (!isTuningRecord(value) || !hasOnlyTuningKeys(value, ['version', 'target', 'endpoint', 'sequence', 'monotonicStartMs',
    'monotonicEndMs', 'wallAnchorMs', 'clock', 'pathEpoch', 'captureGeneration', 'sourceId', 'endpointConnectionEpoch',
    'metrics', 'distributions', 'context', 'telemetryDropped', 'sampleSkipped', 'visibility'])) return false;
  return value['version'] === 1 && isTuningIdentity(value['target']) && isTuningEnum(value['endpoint'], ['host', 'guest']) &&
    isTuningInteger(value['sequence'], 1) && isTuningNumber(value['monotonicStartMs']) &&
    isTuningNumber(value['monotonicEndMs'], value['monotonicStartMs']) && isTuningNumber(value['wallAnchorMs']) && isClock(value['clock']) &&
    isTuningInteger(value['pathEpoch']) && (value['captureGeneration'] === undefined || isTuningInteger(value['captureGeneration'])) &&
    (value['sourceId'] === undefined || isTuningString(value['sourceId'])) && isTuningInteger(value['endpointConnectionEpoch']) &&
    isMetricMap(value['metrics'], isTuningMetric) && isMetricMap(value['distributions'], isTuningHistogram) &&
    (value['context'] === undefined || isTuningTelemetryContext(value['context'])) &&
    isTuningInteger(value['telemetryDropped']) && isTuningInteger(value['sampleSkipped']) && isTuningString(value['visibility'], 32) &&
    tuningJsonBytes(value) <= TUNING_TELEMETRY_MAX_BYTES;
}

export function isTuningTelemetryContext(value: unknown): value is TuningTelemetryContext {
  const keys = ['codec', 'codecProfile', 'encoderImplementation', 'decoderImplementation', 'route', 'recoveryState', 'recoveryReason',
    'policyVersion', 'buildId', 'runtimeId', 'configurationHash'];
  return isTuningRecord(value) && hasOnlyTuningKeys(value, keys) && Object.entries(value).every(([key, label]) =>
    key === 'route' ? isTuningEnum(label, ['direct', 'relay', 'unknown']) : isTuningString(label, 160));
}

export function parseTuningTelemetry(value: unknown): TuningTelemetryWindow | undefined {
  return isTuningTelemetry(value) ? value : undefined;
}
